/**
 * @group DataDeletion
 * @summary User-facing messages for the data deletion feature
 * 
 * <blockquote>
 * Contains all success and error messages for the data deletion workflow.
 * Messages are designed to prevent email enumeration by returning identical
 * responses regardless of whether an email exists in the system.
 * </blockquote>
 * 
 * @example
 * ```typescript
 * import { DATA_DELETION_MESSAGES } from '@shared/constants';
 * 
 * // Always return the same message for requests
 * return Response.json({
 *   success: true,
 *   message: DATA_DELETION_MESSAGES.SUCCESS.REQUEST_SUBMITTED
 * });
 * ```
 */
export const DATA_DELETION_MESSAGES = {
  SUCCESS: {
    /**
     * Generic success message for deletion requests.
     * Same message regardless of whether email exists (enumeration prevention).
     */
    REQUEST_SUBMITTED: 'If an account exists with this email, you will receive a confirmation link.',
    
    /**
     * Message shown after successful data deletion.
     */
    DELETION_COMPLETE: 'Your data has been permanently deleted.',
    
    /**
     * Message shown when resend is successful.
     */
    RESEND_COMPLETE: 'A new confirmation link has been sent if an account exists with this email.',
  },

  ERRORS: {
    /**
     * Generic error for invalid/expired/used tokens.
     * Does not reveal specific reason (security).
     */
    INVALID_TOKEN: 'This link is invalid or has expired. Please request a new deletion link.',
    
    /**
     * Generic error for failed requests.
     */
    REQUEST_FAILED: 'Unable to process your request. Please try again later.',
    
    /**
     * Error for invalid email format.
     */
    INVALID_EMAIL: 'Please enter a valid email address.',
    
    /**
     * Error when rate limit is exceeded.
     */
    RATE_LIMIT_EXCEEDED: 'Too many requests. Please try again later.',
  },

  UI: {
    /**
     * Page title for deletion request page.
     */
    REQUEST_PAGE_TITLE: 'Delete Your Data',
    
    /**
     * Page title for confirmation page.
     */
    CONFIRM_PAGE_TITLE: 'Confirm Data Deletion',
    
    /**
     * Description text for the request page.
     */
    REQUEST_PAGE_DESCRIPTION: 'Request deletion of all your personal data from our systems.',
    
    /**
     * Warning text about irreversibility.
     */
    IRREVERSIBLE_WARNING: 'This action cannot be undone. All your data will be permanently deleted.',
    
    /**
     * Button text for submit.
     */
    SUBMIT_BUTTON: 'Request Deletion',
    
    /**
     * Button text for confirm.
     */
    CONFIRM_BUTTON: 'Confirm Deletion',
    
    /**
     * Loading state text.
     */
    LOADING: 'Processing...',
    
    /**
     * Text explaining what data will be deleted.
     */
    DATA_DELETED_LIST: 'This includes: your account, patient profile, payment methods, health records, and consultation history.',
  },

  EMAIL: {
    /**
     * Email subject line.
     */
    SUBJECT: 'Confirm Your Data Deletion Request',
    
    /**
     * Expiry notice format.
     */
    EXPIRY_NOTICE: 'This link will expire in {hours} hours.',
  },
} as const;

